import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { recalculateAllBalances, fixAdminStores, getStores, apiClient } from '../api/api';
import { RefreshCw, UserX, AlertTriangle, CheckCircle, XCircle, Database, Calculator } from 'lucide-react';

const AdminTools = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [storeDetails, setStoreDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculateResult, setRecalculateResult] = useState(null);
  const [fixStoresResult, setFixStoresResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const storesData = await getStores();
      setStores(storesData);
      
      // Cargar detalles de cada tienda
      const details = await Promise.all(storesData.map(async (store) => {
        try {
          const response = await apiClient.get(`/admin/store-analysis/${store.id}`);
          return response.data;
        } catch (error) {
          return {
            store_id: store.id,
            store_name: store.name,
            error: true
          };
        }
      }));
      setStoreDetails(details);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setIsProcessing(true);
    setRecalculateResult(null);
    try {
      const result = await recalculateAllBalances();
      setRecalculateResult(result);
      await loadData(); // Recargar datos
    } catch (error) {
      setRecalculateResult({ error: error.response?.data?.detail || 'Error al recalcular' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFixStores = async () => {
    setIsProcessing(true);
    setFixStoresResult(null);
    try {
      const result = await fixAdminStores();
      setFixStoresResult(result);
    } catch (error) {
      setFixStoresResult({ error: error.response?.data?.detail || 'Error al corregir tiendas' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="mx-auto text-red-500" size={48} />
        <h1 className="text-2xl font-bold text-red-600 mt-4">Acceso Denegado</h1>
        <p className="text-gray-600">Solo administradores pueden acceder a esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold flex items-center space-x-3">
          <Database size={28} />
          <span>Herramientas de Administrador</span>
        </h1>
        <p className="text-slate-300 mt-2">Diagnóstico y corrección de datos del sistema</p>
      </div>

      {/* Acciones Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
          <h3 className="font-bold text-lg flex items-center space-x-2 text-amber-700">
            <Calculator size={20} />
            <span>Recalcular Balances</span>
          </h3>
          <p className="text-gray-600 text-sm mt-2 mb-4">
            Recalcula el balance de TODAS las tiendas basándose en:<br/>
            <code className="bg-gray-100 px-2 py-1 rounded text-xs">Balance = Inicial + Ventas - Gastos (sin transferencias)</code>
          </p>
          <button
            onClick={handleRecalculate}
            disabled={isProcessing}
            className="w-full px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:bg-gray-400 flex items-center justify-center space-x-2"
          >
            {isProcessing ? <RefreshCw className="animate-spin" size={18} /> : <RefreshCw size={18} />}
            <span>{isProcessing ? 'Procesando...' : 'Recalcular Ahora'}</span>
          </button>
          
          {recalculateResult && (
            <div className={`mt-4 p-3 rounded-lg ${recalculateResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {recalculateResult.error ? (
                <p>❌ {recalculateResult.error}</p>
              ) : (
                <>
                  <p className="font-semibold">✅ {recalculateResult.message}</p>
                  {recalculateResult.stores_adjusted > 0 && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium">Tiendas ajustadas:</p>
                      {recalculateResult.details?.filter(s => s.adjustment !== 0).map((s, i) => (
                        <p key={i}>{s.store_name}: Q{s.old_balance?.toFixed(2)} → Q{s.new_balance?.toFixed(2)}</p>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
          <h3 className="font-bold text-lg flex items-center space-x-2 text-red-700">
            <UserX size={20} />
            <span>Corregir Tiendas Admin</span>
          </h3>
          <p className="text-gray-600 text-sm mt-2 mb-4">
            Quita la tienda asignada de todos los usuarios administrativos (admin, gerencia, supervisores).
            <br/><strong>Los admins no deben tener tienda fija.</strong>
          </p>
          <button
            onClick={handleFixStores}
            disabled={isProcessing}
            className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:bg-gray-400 flex items-center justify-center space-x-2"
          >
            <UserX size={18} />
            <span>Corregir Ahora</span>
          </button>
          
          {fixStoresResult && (
            <div className={`mt-4 p-3 rounded-lg ${fixStoresResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {fixStoresResult.error ? (
                <p>❌ {fixStoresResult.error}</p>
              ) : (
                <p className="font-semibold">✅ {fixStoresResult.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Estado de las Tiendas */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-slate-800 text-white px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">Estado de Tiendas</h3>
          <button
            onClick={loadData}
            className="px-3 py-1 bg-slate-600 hover:bg-slate-500 rounded text-sm"
          >
            ⟳ Actualizar
          </button>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto text-blue-500" size={32} />
            <p className="mt-2 text-gray-600">Cargando análisis...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Tienda</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance Actual</th>
                  <th className="px-4 py-3 text-right font-semibold">Ventas Total</th>
                  <th className="px-4 py-3 text-right font-semibold">Gastos Total</th>
                  <th className="px-4 py-3 text-right font-semibold">Gastos Transfer.</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance Calculado</th>
                  <th className="px-4 py-3 text-center font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {stores.map((store, idx) => {
                  const detail = storeDetails.find(d => d.store_id === store.id) || {};
                  const currentBalance = store.current_balance || 0;
                  const initialBalance = store.initial_cash_flow || 500;
                  const totalSales = detail.total_sales || 0;
                  const totalExpenses = detail.total_expenses || 0;
                  const transferExpenses = detail.transfer_expenses || 0;
                  const calculatedBalance = initialBalance + totalSales - (totalExpenses - transferExpenses);
                  const difference = Math.abs(currentBalance - calculatedBalance);
                  const isCorrect = difference < 1; // Tolerancia de Q1
                  
                  return (
                    <tr key={store.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{store.name}</div>
                        {detail.sales_count > 0 && (
                          <div className="text-xs text-gray-500">
                            {detail.sales_count} ventas | {detail.expenses_count || 0} gastos
                          </div>
                        )}
                        {detail.first_sale_date && (
                          <div className="text-xs text-blue-500">
                            Desde: {new Date(detail.first_sale_date).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {store.currency || 'Q'}{currentBalance.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-green-600">
                        +{totalSales.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-red-600">
                        -{totalExpenses.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-purple-600">
                        {transferExpenses.toFixed(2)}
                        {detail.transfer_count > 0 && (
                          <span className="text-xs ml-1">({detail.transfer_count})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">
                        {store.currency || 'Q'}{calculatedBalance.toFixed(2)}
                        {!isCorrect && (
                          <div className="text-xs text-red-500">
                            Dif: {(currentBalance - calculatedBalance).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {detail.error ? (
                          <span className="text-gray-400">—</span>
                        ) : isCorrect ? (
                          <CheckCircle className="inline text-green-500" size={20} />
                        ) : (
                          <XCircle className="inline text-red-500" size={20} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leyenda */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <p className="font-semibold text-blue-800 mb-2">📊 Cómo interpretar:</p>
        <ul className="text-blue-700 space-y-1">
          <li><CheckCircle className="inline text-green-500 mr-1" size={16} /> Balance actual coincide con el calculado</li>
          <li><XCircle className="inline text-red-500 mr-1" size={16} /> Balance actual NO coincide - necesita recálculo</li>
          <li><strong>Gastos Transfer.</strong> = Gastos marcados como transferencia (no deben afectar el flujo)</li>
          <li><strong>Balance Calculado</strong> = Inicial + Ventas - Gastos (sin transferencias)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminTools;
